import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PlannedEmployeeWeek, Location } from '@functieloonhuis/shared';
import { isSameDay, toDateString } from '../utils/dateUtils';

interface PlanningDayViewProps {
  planned: PlannedEmployeeWeek[];
  date: Date;
  filteredLocation?: Location | null;
}

interface EmployeeDay {
  employeeId: number;
  userId: number;
  name: string;
  departmentName?: string | null;
  isWorkday: boolean;
  location?: Location | null;
  isHalfDay: boolean;
  halfDayPeriod?: 'ochtend' | 'middag' | null;
  startTime?: string | null;
  endTime?: string | null;
  isHoliday: boolean;
  holidayName?: string | null;
}

function LocationGroup({
  title,
  icon,
  employees,
  colorClass,
  onNavigate,
}: {
  title: string;
  icon: React.ReactNode;
  employees: EmployeeDay[];
  colorClass: { card: string; avatar: string; badge: string; text: string };
  onNavigate: (id: number) => void;
}) {
  if (employees.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <span className="text-xs text-gray-400 font-medium">{employees.length}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {employees.map(emp => (
          <button
            key={emp.employeeId}
            type="button"
            onClick={() => onNavigate(emp.employeeId)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-shadow hover:shadow-sm ${colorClass.card}`}
          >
            <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${colorClass.avatar}`}>
              {emp.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate">{emp.name}</span>
              {emp.startTime && emp.endTime && (
                <span className="text-xs opacity-70">{emp.startTime}–{emp.endTime}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function PlanningDayView({ planned, date, filteredLocation }: PlanningDayViewProps) {
  const navigate = useNavigate();
  const dateStr = toDateString(date);

  const dayEntries = useMemo<EmployeeDay[]>(() => {
    return planned.map(row => {
      const day = row.days.find(d => d.date === dateStr);
      return {
        employeeId: row.employee.id,
        userId: row.employee.userId,
        name: row.employee.name,
        departmentName: row.employee.departmentName,
        isWorkday: day?.isWorkday ?? false,
        location: day?.location ?? null,
        isHalfDay: day?.isHalfDay ?? false,
        halfDayPeriod: day?.halfDayPeriod ?? null,
        startTime: day?.startTime ?? null,
        endTime: day?.endTime ?? null,
        isHoliday: day?.isHoliday ?? false,
        holidayName: day?.holidayName ?? null,
      };
    });
  }, [planned, dateStr]);

  const kantoorEmployees = useMemo(
    () => dayEntries.filter(e =>
      e.isWorkday && e.location === 'kantoor' && (!filteredLocation || filteredLocation === 'kantoor')
    ),
    [dayEntries, filteredLocation],
  );
  const thuisEmployees = useMemo(
    () => dayEntries.filter(e =>
      e.isWorkday && e.location === 'thuis' && (!filteredLocation || filteredLocation === 'thuis')
    ),
    [dayEntries, filteredLocation],
  );
  const vrijEmployees = useMemo(
    () => !filteredLocation ? dayEntries.filter(e => !e.isWorkday && !e.isHoliday) : [],
    [dayEntries, filteredLocation],
  );
  const holidayEmployees = useMemo(
    () => !filteredLocation ? dayEntries.filter(e => e.isHoliday) : [],
    [dayEntries, filteredLocation],
  );

  const isToday = isSameDay(date, new Date());

  const handleNavigate = (id: number) =>
    navigate(`/medewerkers/${id}?tab=werkrooster`);

  const total = kantoorEmployees.length + thuisEmployees.length;

  if (total === 0 && !filteredLocation) {
    return (
      <div className="text-center py-8 text-sm text-gray-500">
        {isToday ? 'Niemand staat gepland voor vandaag' : 'Niemand staat gepland voor deze dag'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LocationGroup
        title="Kantoor"
        icon={
          <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        employees={kantoorEmployees}
        colorClass={{
          card: 'bg-green-50 border-green-200 text-green-800',
          avatar: 'bg-green-200 text-green-800',
          badge: 'bg-amber-100 text-amber-800 border border-amber-200',
          text: 'text-green-700',
        }}
        onNavigate={handleNavigate}
      />

      <LocationGroup
        title="Thuis"
        icon={
          <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        }
        employees={thuisEmployees}
        colorClass={{
          card: 'bg-blue-50 border-blue-200 text-blue-800',
          avatar: 'bg-blue-200 text-blue-800',
          badge: 'bg-amber-100 text-amber-800 border border-amber-200',
          text: 'text-blue-700',
        }}
        onNavigate={handleNavigate}
      />

      {vrijEmployees.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-gray-500">Vrij</span>
            <span className="text-xs text-gray-400 font-medium">{vrijEmployees.length}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {vrijEmployees.map(emp => (
              <button
                key={emp.employeeId}
                type="button"
                onClick={() => handleNavigate(emp.employeeId)}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-gray-500 hover:shadow-sm transition-shadow"
              >
                <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500">
                  {emp.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm">{emp.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {holidayEmployees.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-amber-700">Feestdag</span>
            <span className="text-xs font-medium text-amber-600">
              {holidayEmployees[0]?.holidayName}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
